import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

/**
 * How long the row must be hovered before the tooltip appears. Keeps a quick
 * mouse pass (or a sidebar toggle that happens to land under the cursor) from
 * popping a tooltip that's gone a moment later.
 */
const TOOLTIP_SHOW_DELAY_MS = 250
const TOOLTIP_GAP = 16

/**
 * Hover-triggered tooltip for a single collapsed-rail row. Returns a `ref` +
 * mouse handlers meant for the row's own link/button element (so the hover
 * area matches the actual clickable row, not just its icon/label content),
 * and the tooltip node to render anywhere in that row's tree.
 *
 * Portaled to document.body: the rail's own <ul> has overflow-y: auto, which
 * forces overflow-x to compute as auto too (a CSS overflow-shorthand quirk),
 * so anything absolutely positioned inside it would get clipped at the rail's
 * edge instead of floating over the page like the Figma tooltip does.
 */
export function useNavTooltip({ label, disabled }) {
  const linkRef = useRef(null)
  const showTimerRef = useRef(null)
  const [tooltipPos, setTooltipPos] = useState(null)

  useEffect(() => () => clearTimeout(showTimerRef.current), [])

  const handleMouseEnter = useCallback(() => {
    if (disabled) return

    showTimerRef.current = setTimeout(() => {
      const rect = linkRef.current?.getBoundingClientRect()
      if (!rect) return
      setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + TOOLTIP_GAP })
    }, TOOLTIP_SHOW_DELAY_MS)
  }, [disabled])

  const handleMouseLeave = useCallback(() => {
    clearTimeout(showTimerRef.current)
    setTooltipPos(null)
  }, [])

  const tooltipNode =
    tooltipPos &&
    ReactDOM.createPortal(
      <span
        className="main-nav__tooltip"
        role="tooltip"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <span className="main-nav__tooltip-tail" aria-hidden="true" />
        {label}
      </span>,
      document.body,
    )

  return { linkRef, handleMouseEnter, handleMouseLeave, tooltipNode }
}
